import React, {useContext, useEffect, useState} from "react";
import {AppContextContext, ExternalSearchPricingServiceContext} from "../../Contexts";
import {Breadcrumb, Button, message, Modal, Space, Table} from "antd";
import {DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Link, useNavigate} from "react-router-dom";
import * as globalStyles from "../App.module.css";
import ExternalSearchPricing from "../../domain/ExternalSearchPricing";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import {ColumnsType} from "antd/es/table";
import {formatExternalWebSearchBackend} from "../../domain/AiExtractor";
import * as styles from "./ExternalSearchPricingList.module.css";
import CreditsValue from "../common/CreditsValue";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

function ExternalSearchPricingList() {
    const appContext = useContext(AppContextContext);
    const externalSearchPricingService = useContext(ExternalSearchPricingServiceContext);
    const navigate = useNavigate();
    const [tableStateId, setTableStateId] = useState(crypto.randomUUID());

    const title = "External search credits";

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button type={"primary"} icon={<PlusOutlined/>} onClick={() => navigate("/definitions/external-search-pricing/add")}>
                        Add
                    </Button>
                </div>

                <ExternalSearchPricingTable key={tableStateId} columns={getColumns()}/>
            </>
        </DocumentTitle>
    );

    function getColumns(): ColumnsType<ExternalSearchPricing> {
        return [
            {
                dataIndex: "backend",
                title: "Backend",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                defaultSortOrder: "ascend",
                render: (value, pricing: ExternalSearchPricing) =>
                    <Link to={`/definitions/external-search-pricing/${pricing.id}`}>{formatExternalWebSearchBackend(value)}</Link>
            },
            {
                dataIndex: "pricePer1kCalls",
                title: "Per 1K billable calls",
                render: value => <CreditsValue value={value} fixedDecimals={4} showIcon={false}/>
            },
            {
                title: "Actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: renderAction
            }
        ];
    }

    function renderAction(value: any, pricing: ExternalSearchPricing) {
        return (
            <Space>
                <Button
                    icon={<DeleteFilled/>}
                    className={"ant-btn-icon-only"}
                    title={"Delete"}
                    onClick={() => {
                        Modal.confirm({
                            content: <>Do you really want to delete credits for <b>{formatExternalWebSearchBackend(pricing.backend)}</b>?</>,
                            okText: "Delete",
                            cancelText: "Cancel",
                            onOk: () => onDeleteConfirm(pricing)
                        });
                    }}
                />
            </Space>
        );
    }

    function onDeleteConfirm(pricing: ExternalSearchPricing) {
        externalSearchPricingService.delete(pricing)
            .then(() => {
                message.success("External search credits deleted.");
                setTableStateId(crypto.randomUUID());
            });
    }
}

export default ExternalSearchPricingList;

interface TableProps {
    columns: ColumnsType<ExternalSearchPricing>;
}

function ExternalSearchPricingTable(props: TableProps) {
    const externalSearchPricingService = useContext(ExternalSearchPricingServiceContext);
    const [pricing, setPricing] = useState<PagedResult<ExternalSearchPricing>>();

    const tableHandler = useTableHandler("backend", {
        reloadFunction: reload,
        persistentIdent: "ExternalSearchPricingList",
    });

    useEffect(() => tableHandler.reload(), []);

    return (
        <>
            <div className={globalStyles["common__top-button-bar"]}>
                <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
            </div>

            <Table
                dataSource={pricing?.data}
                loading={tableHandler.loading}
                pagination={tableHandler.pagination}
                showSorterTooltip={false}
                rowKey="id"
                onChange={tableHandler.onTableChange}
                columns={props.columns}
                className={styles.table}
            />
        </>
    );

    function reload() {
        return externalSearchPricingService.getList(tableHandler.queryOptions)
            .then(result => {
                tableHandler.updateTotal(result.total);
                setPricing(result);
            });
    }
}
